import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Edit, AlertCircle, User } from 'lucide-react';
import { accounts } from '../../services/apiService';
import { Loader as SectionLoader } from '../UI/LoadingSpinner';
import CreateMyAccountsModal from './CreateMyAccountsModal';
import EditMyAccountsModal from './EditMyAccountsModal';
import AccountAccordionItem from '../UI/AccountAccordionItem';

const MyAccountsTab = ({ userId }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [expandedAccountId, setExpandedAccountId] = useState(null);

  const {
    data: accountsResponse,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['myAccounts'],
    queryFn: accounts.getMyAccounts,
    retry: 1,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0
  });

  const myAccounts = accountsResponse?.data || [];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <SectionLoader variant="component" size="md" fullHeight={false} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
        <p className="text-light-text-secondary dark:text-dark-text-secondary">
          {error?.message || 'Failed to load accounts'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with Create Button */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary flex items-center">
          <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg mr-3 shadow-md">
            <User className="h-5 w-5 text-white" />
          </div>
          <span className="bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
            My Accounts
          </span>
          <span className="ml-2 px-2.5 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm font-semibold rounded-full">
            {myAccounts.length}
          </span>
        </h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="group relative inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 dark:from-primary-500 dark:to-primary-600 dark:hover:from-primary-600 dark:hover:to-primary-700 text-white text-sm font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
        >
          <Plus className="h-4 w-4 mr-2 group-hover:rotate-90 transition-transform duration-300" />
          Create Account
        </button>
      </div>

      {/* Empty State */}
      {myAccounts.length === 0 && (
        <div className="relative bg-gradient-to-br from-light-bg-primary via-light-bg-tertiary to-light-bg-secondary dark:from-dark-bg-tertiary dark:via-dark-bg-secondary dark:to-dark-bg-tertiary border-2 border-dashed border-light-border-primary dark:border-dark-border-primary rounded-2xl p-12 text-center overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/5 rounded-full blur-3xl"></div>
          <div className="relative">
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-primary-500/20 blur-2xl rounded-full"></div>
              <div className="relative p-4 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 rounded-2xl">
                <User className="h-12 w-12 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2">No accounts found</h3>
            <p className="text-light-text-secondary dark:text-dark-text-secondary mb-6">Create your first account to get started</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="group inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 dark:from-primary-500 dark:to-primary-600 dark:hover:from-primary-600 dark:hover:to-primary-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
            >
              <Plus className="h-5 w-5 mr-2 group-hover:rotate-90 transition-transform duration-300" />
              Create Account
            </button>
          </div>
        </div>
      )}

      {/* Accounts List — Accordion */}
      {myAccounts.length > 0 && (
        <div className="space-y-3">
          {myAccounts.map((account, index) => {
            const isExpanded = expandedAccountId === account.id;

            return (
              <AccountAccordionItem
                key={account.id}
                account={account}
                accountKey={account.id}
                isExpanded={isExpanded}
                onToggle={(key) => setExpandedAccountId(isExpanded ? null : key)}
                index={index}
                isOwner={true}
                showSecurity={false}
                showTimeline={false}
                badges={
                  account.socialAccounts && account.socialAccounts.length > 0 ? (
                    <span className="hidden sm:inline-flex px-2 py-1 text-xs font-semibold rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                      {account.socialAccounts.length} Social
                    </span>
                  ) : null
                }
                headerActions={
                  <button
                    onClick={() => setEditingAccount(account)}
                    className="p-2 rounded-lg text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-primary transition-all duration-200"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                }
              />
            );
          })}
        </div>
      )}

      {/* Modals */}
      <CreateMyAccountsModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      <EditMyAccountsModal
        isOpen={!!editingAccount}
        onClose={() => setEditingAccount(null)}
        account={editingAccount}
      />
    </div>
  );
};

export default MyAccountsTab;
